/**
 * Turning the teacher's assigned regions into the shared `ScopePicker`'s rows,
 * and resolving which one the URL's `?region=` names.
 *
 * The picker's rows want a hierarchy (`parentId`/`depth`) and a director name;
 * a teacher's own region list is flat (there's no stored ancestor or director
 * for it) and read-only here, so every row is a top-level one with no
 * caption — nesting and directors belong to the admin-side roster this page
 * doesn't have.
 */
import { slugifyRegionName } from '../../../lib/regionSlug';
import type { ScopeRegion } from '../primitives/scope/scope.types';

/** One assigned region, addressable by the same slug the workshop chips carry
 *  in `regionSlug` (both derive it from the region's name). */
export function teacherScopeRegions(regionIds: string[], regionNames: string[]): ScopeRegion[] {
  return regionIds.map((id, i) => ({
    id,
    name: regionNames[i],
    slug: slugifyRegionName(regionNames[i]),
    parentId: null,
    depth: 0,
    directorName: null,
  }));
}

/** The region the `?region=` slug names, or the first assigned region when the
 *  param is absent or doesn't match one the teacher can see. `null` only when
 *  the teacher has no assigned regions at all. */
export function resolveCurrentRegion(
  regions: ScopeRegion[],
  slugParam: string | null,
): ScopeRegion | null {
  const bySlug = slugParam && regions.find((r) => r.slug === slugParam);
  return bySlug || regions[0] || null;
}
